import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type { NotificationFeedResponse, MarkReadResponse, UnreadCountResponse } from '../types'
import { useConnectivity } from '../../hooks/useConnectivity'

export function useNotificationFeed(vertical?: string | null) {
  return useInfiniteQuery({
    queryKey: ['notifications', vertical ?? null],
    queryFn: ({ pageParam }) =>
      apiFetch<NotificationFeedResponse>('api/v1/notifications', {
        params: {
          limit: 20,
          cursor: pageParam,
          vertical: vertical || undefined,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.next_cursor ? lastPage.next_cursor : undefined),
  })
}

export function useUnreadCount(vertical?: string | null) {
  return useQuery({
    queryKey: ['notifications', 'unread-count', vertical ?? null],
    queryFn: () =>
      apiFetch<UnreadCountResponse>('api/v1/notifications/unread-count', {
        params: { vertical: vertical || undefined },
      }),
    refetchInterval: 30_000,
  })
}

export function useMarkRead() {
  const queryClient = useQueryClient()
  const isOnline = useConnectivity()
  return useMutation({
    mutationFn: (notificationIds: string[]) => {
      if (!isOnline) {
        throw new Error('No connection — please check your network')
      }
      return apiFetch<MarkReadResponse>('api/v1/notifications/read', {
        method: 'POST',
        body: { notification_ids: notificationIds },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })
}

export function useMarkAllRead() {
  const queryClient = useQueryClient()
  const isOnline = useConnectivity()
  return useMutation({
    mutationFn: () => {
      if (!isOnline) {
        throw new Error('No connection — please check your network')
      }
      return apiFetch<MarkReadResponse>('api/v1/notifications/read-all', {
        method: 'POST',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })
}
